import React, { useState, useEffect } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { ChevronRight, ChevronLeft, Check, Sparkles, LayoutDashboard, Users, FolderKanban, ShieldCheck } from 'lucide-react';
import { createPageUrl } from '../utils/index';
import { useTranslation } from 'react-i18next';

// Fullscreen Modal Slides
const getOnboardingSlides = (t) => [
    {
        id: 'welcome',
        title: t('app.onboarding.welcome_title'),
        description: t('app.onboarding.welcome_desc'),
        icon: Sparkles,
        color: 'text-red-500'
    },
    {
        id: 'clients',
        title: t('app.onboarding.clients_title'),
        description: t('app.onboarding.clients_desc'),
        icon: Users,
        color: 'text-blue-500'
    },
    {
        id: 'projects',
        title: t('app.onboarding.projects_title'),
        description: t('app.onboarding.projects_desc'),
        icon: FolderKanban,
        color: 'text-emerald-500'
    },
    {
        id: 'security',
        title: t('app.onboarding.security_title'),
        description: t('app.onboarding.security_desc'),
        icon: ShieldCheck,
        color: 'text-purple-500'
    }
];

export default function OnboardingFlow() {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const onboardingSlides = getOnboardingSlides(t);

    useEffect(() => {
        // Check if user has already seen the onboarding
        const hasCompletedOnboarding = localStorage.getItem('core829_onboarding_completed');
        if (!hasCompletedOnboarding) {
            setShowModal(true);
        }
    }, []);

    const handleNextSlide = () => {
        if (currentSlide < onboardingSlides.length - 1) {
            setCurrentSlide(prev => prev + 1);
        } else {
            startProductTour();
        }
    };

    const handlePrevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(prev => prev - 1);
        }
    };

    const startProductTour = () => {
        setShowModal(false);
        localStorage.setItem('core829_onboarding_completed', 'true');

        setTimeout(() => {
            const tourDriver = driver({
                showProgress: true,
                animate: true,
                doneBtnText: t('app.onboarding.tour_done'),
                closeBtnText: t('app.onboarding.tour_close'),
                nextBtnText: t('app.onboarding.tour_next'),
                prevBtnText: t('app.onboarding.tour_prev'),
                progressText: t('app.onboarding.tour_progress'),
                popoverClass: 'core829-driver-theme',
                steps: [
                    {
                        element: `a[href="${createPageUrl('Dashboard')}"]`,
                        popover: {
                            title: t('app.onboarding.tour_dashboard_title'),
                            description: t('app.onboarding.tour_dashboard_desc'),
                            side: 'right',
                            align: 'start'
                        }
                    },
                    {
                        element: `a[href="${createPageUrl('Clients')}"]`,
                        popover: {
                            title: t('app.onboarding.tour_clients_title'),
                            description: t('app.onboarding.tour_clients_desc'),
                            side: 'right',
                            align: 'start'
                        }
                    },
                    {
                        element: `a[href="${createPageUrl('Pipeline')}"]`,
                        popover: {
                            title: t('app.onboarding.tour_pipeline_title'),
                            description: t('app.onboarding.tour_pipeline_desc'),
                            side: 'right',
                            align: 'start'
                        }
                    },
                    {
                        element: `a[href="${createPageUrl('Projects')}"]`,
                        popover: {
                            title: t('app.onboarding.tour_projects_title'),
                            description: t('app.onboarding.tour_projects_desc'),
                            side: 'right',
                            align: 'start'
                        }
                    },
                    {
                        popover: {
                            title: t('app.onboarding.tour_ready_title'),
                            description: t('app.onboarding.tour_ready_desc')
                        }
                    }
                ]
            });

            tourDriver.drive();
        }, 500); // small delay to let the modal fade out gracefully
    };

    if (!showModal) return null;

    const slide = onboardingSlides[currentSlide];
    const SlideIcon = slide.icon;

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 backdrop-blur-xl transition-all duration-500 p-4">

            <style>{`
                /* Driver.js custom theme overrides for Core829 */
                .core829-driver-theme {
                    background-color: #1a1a1a !important;
                    color: white !important;
                    border: 1px solid #262626 !important;
                    border-radius: 12px !important;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.5), 0 0 20px rgba(220, 38, 38, 0.15) !important;
                }
                .core829-driver-theme .driver-popover-title {
                    color: white !important;
                    font-size: 1.1rem !important;
                    font-weight: bold !important;
                    margin-bottom: 0.5rem !important;
                }
                .core829-driver-theme .driver-popover-description {
                    color: #a3a3a3 !important;
                    font-size: 0.9rem !important;
                    line-height: 1.5 !important;
                }
                .core829-driver-theme .driver-popover-footer {
                    margin-top: 1rem !important;
                }
                .core829-driver-theme .driver-popover-progress-text {
                    color: #737373 !important;
                }
                .core829-driver-theme button {
                    background-color: #262626 !important;
                    color: white !important;
                    text-shadow: none !important;
                    border: 1px solid #404040 !important;
                    border-radius: 6px !important;
                    padding: 5px 10px !important;
                    transition: all 0.2s !important;
                }
                .core829-driver-theme button:hover {
                    background-color: #404040 !important;
                }
                /* Next/Finish primary button styling */
                .core829-driver-theme .driver-popover-next-btn {
                    background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%) !important;
                    border: none !important;
                }
                .core829-driver-theme .driver-popover-next-btn:hover {
                    opacity: 0.9 !important;
                }
                .core829-driver-theme .driver-popover-arrow {
                    border-color: #1a1a1a !important;
                }
            `}</style>

            <div className="relative w-full max-w-3xl bg-dark-card borderborder-dark-line rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row min-h-[400px]">

                {/* Left side graphical banner */}
                <div className="w-full md:w-2/5 bg-dark-bg flex flex-col justify-center items-center p-8 border-b md:border-b-0 md:border-rborder-dark-line relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-red-500/10 to-transparent pointer-events-none" />
                    <img
                        src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/693abcae586ea1ac8122ea70/21aa16bfb_CORE829LARGEPXlogonolettersnobg.png"
                        alt="Core829 Logo"
                        className="w-20 md:w-32 mb-6 drop-shadow-2xl opacity-80"
                    />
                    <h2 className="text-xl md:text-2xl font-bold text-center tracking-tight">
                        Core829<span className="text-red-500">.</span>
                    </h2>
                </div>

                {/* Right side content */}
                <div className="w-full md:w-3/5 p-8 flex flex-col justify-between">
                    <div key={currentSlide} className="animate-in fade-in slide-in-from-right-4 duration-500">
                        <div className="mb-6 inline-flex p-3 rounded-xl bg-dark-el shadow-inner">
                            <SlideIcon size={32} className={slide.color} />
                        </div>
                        <h3 className="text-2xl font-bold mb-4">{slide.title}</h3>
                        <p className="text-gray-400 text-lg leading-relaxed">
                            {slide.description}
                        </p>
                    </div>

                    {/* Navigation Footer */}
                    <div className="mt-12 flex items-center justify-between">
                        {/* Dots indicator */}
                        <div className="flex gap-2">
                            {onboardingSlides.map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${currentSlide === idx ? 'w-8 bg-red-500' : 'w-2 bg-[#333]'}`}
                                />
                            ))}
                        </div>

                        {/* Controls */}
                        <div className="flex gap-3">
                            <button
                                onClick={handlePrevSlide}
                                disabled={currentSlide === 0}
                                className={`p-2 rounded-lg borderborder-dark-line transition-colors ${currentSlide === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-dark-el'}`}
                            >
                                <ChevronLeft size={20} />
                            </button>

                            <button
                                onClick={handleNextSlide}
                                className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 font-medium tracking-wide shadow-lg shadow-red-500/20 transition-all"
                            >
                                {currentSlide === onboardingSlides.length - 1 ? (
                                    <>
                                        {t('app.onboarding.interactive_tour')} <Check size={18} />
                                    </>
                                ) : (
                                    <>
                                        {t('app.onboarding.next')} <ChevronRight size={18} />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}
